import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Input, Select, DatePicker, Button, Table, Tag, Space, message, Modal, Dropdown, Pagination, Typography } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { MoreOutlined } from '@ant-design/icons';
import { getMyDebts, revokeDebt, duplicateDebt, createDebtShare } from '../../services/debt';
import { DebtTypeOptions, labelOfDebtStatus, labelOfDebtType, labelOfCollectionStatus, toSelectOptions } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './MyDebtsPage.css';

/**
 * 我的债权
 * 职责：查询与管理已发布/待审核债权；支持筛选、排序、详情查看、分享、撤销发布、复制为草稿等操作。
 * 说明：列表数据来自后端分页接口；前端维护分页/排序状态并传参。
 */
const DEFAULT_PAGINATION = { current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 };
const DEFAULT_SORTER = { field: 'update_time', order: 'desc' };
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);

const ActionCell = ({ record, layoutKey, actionWidth, onPublishNotice, onOpenFollowup, onDuplicateDraft, onRevokePublish, onWechatShare }) => {
  const containerRef = useRef(null);
  const canDuplicateDraft = String(record?.status) === 'PUBLISHED';

  const candidates = useMemo(() => ([
    { key: 'publishNotice', label: '发布催告' },
    { key: 'followupLogs', label: '跟进日志' },
    ...(canDuplicateDraft ? [{ key: 'duplicateDraft', label: '复制为草稿' }] : []),
    { key: 'wechatShare', label: '微信分享' },
    { key: 'revokePublish', label: '撤销发布' },
  ]), [canDuplicateDraft]);

  const [inlineKeys, setInlineKeys] = useState(() => candidates.map(a => a.key));

  useEffect(() => {
    setInlineKeys(candidates.map(a => a.key));
  }, [candidates, layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isOverflow = el.scrollWidth > el.clientWidth + 1;
    if (isOverflow && inlineKeys.length > 1) {
      const next = inlineKeys.slice(0, -1);
      if (next.join('|') !== inlineKeys.join('|')) {
        setInlineKeys(next);
      }
    }
  }, [candidates, inlineKeys]);

  const inlineActions = candidates.filter(a => inlineKeys.includes(a.key));
  const dropdownActions = candidates.filter(a => !inlineKeys.includes(a.key));

  const handleAction = async (key) => {
    if (key === 'publishNotice') {
      onPublishNotice?.(record);
      return;
    }
    if (key === 'followupLogs') {
      onOpenFollowup?.(record);
      return;
    }
    if (key === 'duplicateDraft') {
      await onDuplicateDraft?.(record);
      return;
    }
    if (key === 'wechatShare') {
      await onWechatShare?.(record);
      return;
    }
    if (key === 'revokePublish') {
      await onRevokePublish?.(record);
    }
  };

  return (
    <div
      ref={containerRef}
      className="c-actions-inline c-actions-cell"
      style={{ '--actions-cell-width': actionWidth }}
    >
      {inlineActions.map(action => (
        <Button
          key={action.key}
          type="link"
          className="c-btn-link-compact"
          onClick={() => handleAction(action.key)}
        >
          {action.label}
        </Button>
      ))}

      {dropdownActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dropdownActions.map(a => ({ key: a.key, label: a.label })),
            onClick: async ({ key }) => {
              await handleAction(key);
            },
          }}
        >
          <Button type="link" className="c-btn-link-compact c-actions-more" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      )}
    </div>
  );
};

const MyDebtsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorter, setSorter] = useState(DEFAULT_SORTER);
  const [dateRangeSelecting, setDateRangeSelecting] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareQrUrl, setShareQrUrl] = useState('');

  const disabledOccurrenceDate = useCallback((current) => {
    if (!current) return false;
    const range = Array.isArray(dateRangeSelecting) ? dateRangeSelecting : [];
    const start = range[0];
    const end = range[1];
    if (start && !end) {
      const min = start.subtract(1, 'month');
      const max = start.add(1, 'month');
      return current.isBefore(min, 'day') || current.isAfter(max, 'day');
    }
    return false;
  }, [dateRangeSelecting]);

  // 拉取列表：携带筛选与排序参数，仅保留 PUBLISHED/PENDING 状态记录
  const fetchList = useCallback(async (pageCfg, sortCfg) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        debt_type: values.debt_type,
        keyword: values.keyword,
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getMyDebts(params);
      const all = Array.isArray(data?.records) ? data.records : [];
      const recs = all.filter(r => r.status === 'PUBLISHED' || r.status === 'PENDING');
      setRecords(recs);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载我的债权失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  // 首次进入页面：按默认分页与排序加载数据
  useEffect(() => {
    fetchList({ current: 1, pageSize: DEFAULT_PAGINATION.pageSize }, DEFAULT_SORTER);
  }, [fetchList]);

  // 查询：重置到第一页并按照当前排序条件查询
  const handleSearch = (values) => {
    const range = values?.date_range;
    const start = Array.isArray(range) ? range[0] : null;
    const end = Array.isArray(range) ? range[1] : null;
    if (start && end) {
      if (end.isBefore(start, 'day')) {
        message.error('发生日开始日期不能晚于结束日期');
        return;
      }
      if (end.isAfter(start.add(1, 'month'), 'day')) {
        message.error('发生日查询范围不能超过一个月');
        return;
      }
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize }, sorter);
  };

  // 重置筛选与排序：恢复默认排序并重新查询
  const handleReset = () => {
    form.resetFields();
    setDateRangeSelecting(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter(DEFAULT_SORTER);
    fetchList({ current: 1, pageSize: pagination.pageSize }, DEFAULT_SORTER);
  };

  // 表格交互：同步分页与排序到本地状态，并重新拉取数据
  const handleTableChange = (pag, filters, sorterArg) => {
    const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const field = sorterArg?.field || sorter.field;
    const nextSorter = { field, order };
    setSorter(nextSorter);
    fetchList(pagination, nextSorter);
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    const nextPagination = { ...pagination, current: nextCurrent, pageSize: nextPageSize };
    setPagination(nextPagination);
    fetchList(nextPagination, sorter);
  };

  // 撤销发布：服务层执行状态流转，成功后刷新列表
  const handleRevoke = async (id) => {
    try {
      await revokeDebt(id);
      message.success('已撤销发布，债权转为草稿');
      const next = { current: 1, pageSize: pagination.pageSize };
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchList(next, sorter);
    } catch (e) {
      message.error('撤销发布失败');
    }
  };

  // 将后端返回的 token 拼成可公开访问的前端路由地址
  const buildDebtShareUrl = (token) => {
    if (!token) return '';
    const origin = window.location?.origin || '';
    return `${origin}/share/debt/${token}`;
  };

  // 复制分享链接：优先使用 Clipboard API，降级到 textarea
  const handleCopyShareUrl = async () => {
    const text = String(shareUrl || '');
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('已复制链接');
    } catch (_) {
      message.error('复制失败');
    }
  };

  // 生成债权分享链接并展示二维码
  const openShare = async (debtId) => {
    if (!debtId) return;
    setShareOpen(true);
    setShareLoading(true);
    setShareUrl('');
    setShareQrUrl('');
    try {
      const { data } = await createDebtShare(debtId);
      const token = data?.token || '';
      const url = buildDebtShareUrl(token);
      if (!url) {
        message.error('生成分享链接失败');
        return;
      }
      const qrWidth = isMobile ? 180 : 220;
      const qr = await QRCode.toDataURL(url, { margin: 1, width: qrWidth });
      setShareUrl(url);
      setShareQrUrl(qr);
    } catch (e) {
      message.error(e?.message || '生成分享链接失败');
    } finally {
      setShareLoading(false);
    }
  };

  // 复制为草稿：后端创建新草稿并跳转到发布页编辑
  const handleDuplicate = async (debtId) => {
    try {
      const { data } = await duplicateDebt(debtId);
      const newId = data?.id || data?.debtId || null;
      if (!newId) {
        message.error('复制失败');
        return;
      }
      message.success('已复制为草稿，正在进入编辑');
      navigate(`/debt-management/publish?id=${newId}`);
    } catch (e) {
      message.error('复制失败');
    }
  };

  const columns = [
    { title: '编号', dataIndex: 'debtNo', key: 'debtNo', width: 160 },
    {
      title: '债权名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v, record) => (
        <Typography.Link className="u-ellipsis-inline font-medium" onClick={() => navigate(`/debt-management/detail/${record.id}`)}>
          {v || '-'}
        </Typography.Link>
      ),
    },
    { title: '债权类型', dataIndex: 'debtType', key: 'debtType', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '本金金额', dataIndex: 'principalAmount', key: 'principalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '合计金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '发生日', dataIndex: 'debtOccurrenceDate', key: 'debtOccurrenceDate', width: 120, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '债权状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => {
      const color = v === 'PUBLISHED' ? 'green' : v === 'PENDING' ? 'orange' : 'default';
      return <Tag color={color}>{labelOfDebtStatus(v)}</Tag>;
    } },
    { title: '催收状态', dataIndex: 'collectionStatus', key: 'collectionStatus', width: 120, render: (v) => {
      const color = v === 'IN_COLLECTION' ? 'green' : v === 'PAUSED' ? 'orange' : v === 'CLOSED' ? 'red' : 'default';
      return <Tag color={color}>{labelOfCollectionStatus(v)}</Tag>;
    } },    
    { title: '更新时间', dataIndex: 'updateTime', key: 'updateTime', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 220 : 340,
      render: (_, record) => {
        return (
          <ActionCell
            record={record}
            layoutKey={isMobile ? 'mobile' : 'desktop'}
            actionWidth={isMobile ? '220px' : '340px'}
            onPublishNotice={() => navigate('/debt-notice/publish')}
            onOpenFollowup={() => {
              const sp = new URLSearchParams();
              sp.set('tab', '2');
              sp.set('debtId', String(record.id));
              navigate(`/debt-management/followup?${sp.toString()}`);
            }}
            onDuplicateDraft={() => handleDuplicate(record.id)}
            onWechatShare={() => openShare(record.id)}
            onRevokePublish={() => handleRevoke(record.id)}
          />
        );
      },
    },
  ];

  return (
    <div className="page-container p-debt-management-my-debts">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">我的债权</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
            <Form.Item name="keyword" label="关键词">
              <Input placeholder="债权名称/编号" allowClear style={{ width: isMobile ? '100%' : 200 }} />
            </Form.Item>
            <Form.Item name="debt_type" label="类型">
              <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
            </Form.Item>
            <Form.Item name="date_range" label="发生日">
              <DatePicker.RangePicker
                allowEmpty={[true, true]}
                popupClassName="mydebts-range-picker-popup"
                style={{ width: isMobile ? '100%' : undefined }}
                disabledDate={disabledOccurrenceDate}
                onCalendarChange={(dates) => setDateRangeSelecting(dates)}
                onChange={(dates) => setDateRangeSelecting(dates)}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">查询</Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>
      </div>

      <Modal
        title="微信分享"
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        width={isMobile ? 360 : 420}
        footer={null}
        wrapClassName="mydebts-share-modal"
      >
        <div className="mydebts-share-body">
          <div className="mydebts-share-qr">
            {shareQrUrl ? <img src={shareQrUrl} alt="qrcode" /> : <div className="mydebts-share-loading">生成中...</div>}
          </div>
          <div className="mydebts-share-tip">二维码可微信扫码转发好友/朋友圈</div>
          <div className="mydebts-share-actions">
            <Button onClick={() => setShareOpen(false)} disabled={shareLoading}>取消</Button>
            <Button type="primary" onClick={handleCopyShareUrl} disabled={!shareUrl} loading={shareLoading}>复制链接</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MyDebtsPage;
